import { TextController } from '@/components/forms/FormController'
import Modal from '@/components/layout/Modal'
import { zodResolver } from '@/lib/zod'
import { PencilIcon } from '@heroicons/react/24/outline'
import { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@droidmap/frontend-ui'

export type EditNameEntityType =
  'Project' | 'Asset Set' | 'Task' | 'Group' | 'Organisation'

interface EditNameModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: EditNameEntityType
  initialName: string
  isPending?: boolean
  error?: unknown
  onSubmit: (newName: string) => void
}

const schemaFor = (entityType: EditNameEntityType) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, { error: `${entityType} name is required` })
      .max(100, {
        error: `${entityType} name must be 100 characters or less`,
      }),
  })

type FormValues = { name: string }

const EditNameModal: FC<EditNameModalProps> = ({
  isOpen,
  onClose,
  entityType,
  initialName,
  isPending = false,
  error,
  onSubmit,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schemaFor(entityType)),
    defaultValues: { name: initialName },
  })

  useEffect(() => {
    if (isOpen) reset({ name: initialName })
  }, [isOpen, initialName, reset])

  const submit = handleSubmit(({ name }) => {
    const trimmed = name.trim()
    if (trimmed === initialName) {
      onClose()
      return
    }
    onSubmit(trimmed)
  })

  return (
    <Modal
      title={`Rename ${entityType}`}
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="sm:max-w-lg"
      icon={<PencilIcon />}
    >
      <form onSubmit={submit} className="space-y-4">
        <TextController
          name="name"
          control={control}
          label={`${entityType} Name`}
          errors={errors}
          disabled={isPending}
          autoComplete="off"
        />
        {error !== undefined && error !== null && (
          <div className="text-danger text-sm">
            {error instanceof Error
              ? error.message
              : `An error occurred while renaming the ${entityType.toLowerCase()}.`}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={!isDirty || isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default EditNameModal
